/**
 * LandingFooter — DARK (Teal) brutalist footer for the RetroUI landing tree.
 *
 * Section pattern: the page alternates light background <-> dark Teal slabs; the
 * footer is the dark Teal one. Three columns (BRAND / INDEX / SOCIAL) separated
 * by vertical black divider rules on md+, then a bottom copyright bar.
 *
 * RENDER CONTEXT: this mounts inside a `<div className="retroui-root">` ancestor,
 * so brand tokens + the scoped reset apply. Tailwind PREFLIGHT IS OFF, so every
 * border MUST also declare `border-solid` (e.g. `border-t-2 border-solid
 * border-black`) — otherwise the default `border-style` is `none` and the rule is
 * invisible. Square corners (radius 0) + 2px black rules = brutalist.
 *
 * Pure React + Tailwind utilities + lucide-react. ZERO MUI.
 */
import * as React from 'react';
import {
  ArrowUpRight,
  Instagram,
  Linkedin,
  Mail,
} from 'lucide-react';

const XIcon: React.FC<{ size?: number; className?: string }> = ({ size = 18, className }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden className={className}>
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

/**
 * COLUMN 2 — INDEX links. Real routes/anchors:
 *  - "People" is the dedicated /people team page.
 *  - "Schedule a Demo" is the standalone /schedule-demo page (its own custom URL).
 *  - /researcher/auth and /participant/enroll are real app routes.
 *  (The former in-page "Platform" #features link was removed.)
 */
const INDEX_LINKS: ReadonlyArray<{ label: string; href: string }> = [
  { label: 'People', href: '/people' },
  { label: 'Schedule a Demo', href: '/schedule-demo' },
  { label: 'Researcher Login', href: '/researcher/auth' },
  { label: 'Join as Participant', href: '/participant/enroll' },
];

/** Height of the sticky nav (matches LandingNav's `h-[88px]`) — in-page scrolls
 *  subtract it so the target section clears the bar instead of hiding behind it. */
const NAV_HEIGHT = 88;

/**
 * In-page (#…) INDEX links smooth-scroll to their section (offset for the sticky
 * nav) when it exists on the current page; otherwise — e.g. when this footer is
 * reused on a legal page — navigate to the landing's anchor (`/#features`) so the
 * link is never dead. Route links pass through. Mirrors LandingNav's handler.
 */
function handleIndexClick(e: React.MouseEvent<HTMLAnchorElement>, href: string) {
  if (!href.startsWith('#')) return;
  const target = document.getElementById(href.slice(1));
  if (!target) {
    e.preventDefault();
    window.location.assign(`/${href}`);
    return;
  }
  e.preventDefault();
  const top = target.getBoundingClientRect().top + window.scrollY - NAV_HEIGHT;
  const prefersReduced =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  window.scrollTo({ top, behavior: prefersReduced ? 'auto' : 'smooth' });
  window.history.pushState(null, '', href);
}

/**
 * COLUMN 3 — SOCIAL links. Real StudySync accounts; each opens in a new tab
 * (the mailto naturally opens the mail client). Icons from lucide-react.
 */
const SOCIAL_LINKS: ReadonlyArray<{
  label: string;
  href: string;
  // Accepts both lucide icons (ForwardRefExoticComponent) and the local XIcon (FC).
  Icon: React.ElementType;
}> = [
  { label: 'Instagram', href: 'https://www.instagram.com/study.syncs/', Icon: Instagram },
  { label: 'LinkedIn', href: 'https://www.linkedin.com/company/118294053/', Icon: Linkedin },
  { label: 'X', href: 'https://x.com/StudySyncs', Icon: XIcon },
  { label: 'Email', href: 'mailto:support@studysyncs.com', Icon: Mail },
];

/** Shared classes for the small cream-filled section badges (INDEX / SOCIAL). */
const BADGE_CLASSES =
  'inline-block border-2 border-solid border-black bg-secondary-foreground px-3 py-1 ' +
  'font-sans text-xs font-bold uppercase tracking-[0.18em] text-secondary';

const LandingFooter: React.FC = () => (
  <footer className="border-t-2 border-solid border-black bg-secondary text-secondary-foreground">
    {/* ───────────────────────── TOP REGION: 3-column grid ───────────────────────── */}
    <div className="mx-auto grid max-w-[1280px] gap-10 px-6 py-14 md:grid-cols-3 md:gap-0 md:px-10 md:py-20">
      {/* COLUMN 1 — BRAND ----------------------------------------------------- */}
      <div className="md:pr-10">
        {/* Footer wordmark logo — the Footer variant has WHITE "Study Sync" text
            (the regular variant is all-red) so it reads cleanly on Teal; the icon
            (white box, red brackets + pulse) is unchanged. */}
        <img
          src="/finalized_logo/Study Sync Short Logo Footer.svg"
          alt="StudySync"
          className="h-12 w-auto"
        />

        {/* White accent underline, brutalist short bar. */}
        <div className="mt-5 h-1.5 w-12 bg-white" aria-hidden />

        {/* Tagline with a left accent rule. */}
        <p className="mt-6 max-w-xs border-l-2 border-solid border-white/40 pl-4 font-sans text-sm text-secondary-foreground/90 md:text-base">
          Infrastructure for large-scale health research — collection, storage,
          and per-participant export.
        </p>
      </div>

      {/* COLUMN 2 — INDEX ----------------------------------------------------- */}
      <div className="md:border-l-2 md:border-solid md:border-black md:px-10">
        <span className={BADGE_CLASSES}>Index</span>

        <nav className="mt-6">
          {INDEX_LINKS.map((link) => (
            <a
              key={link.label}
              href={link.href}
              onClick={(e) => handleIndexClick(e, link.href)}
              className="flex items-center justify-between border-b-2 border-solid border-white/25 py-3 font-sans text-sm font-bold uppercase tracking-wide text-secondary-foreground no-underline transition-colors hover:text-yellow"
            >
              <span>{link.label}</span>
              <ArrowUpRight size={18} aria-hidden />
            </a>
          ))}
        </nav>
      </div>

      {/* COLUMN 3 — SOCIAL ---------------------------------------------------- */}
      <div className="md:border-l-2 md:border-solid md:border-black md:px-10">
        <span className={BADGE_CLASSES}>Social</span>

        <nav className="mt-6">
          {SOCIAL_LINKS.map(({ label, href, Icon }) => (
            <a
              key={label}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 py-2 font-sans text-sm text-secondary-foreground no-underline transition-colors hover:text-yellow"
            >
              <Icon size={18} aria-hidden />
              <span>{label}</span>
            </a>
          ))}
        </nav>
      </div>
    </div>

    {/* ───────────────────────── BOTTOM BAR: copyright + legal ───────────────────────── */}
    <div className="border-t-2 border-solid border-black">
      <div className="mx-auto flex max-w-[1280px] flex-col gap-3 px-6 py-5 font-sans text-xs uppercase tracking-wide text-secondary-foreground/80 sm:flex-row sm:items-center sm:justify-between md:px-10">
        <span>© 2026 StudySync. All rights reserved.</span>

        <div className="flex gap-6">
          <a
            href="/privacy-policy"
            className="text-secondary-foreground/80 no-underline transition-colors hover:text-yellow"
          >
            Privacy Policy
          </a>
          <a
            href="/terms-of-service"
            className="text-secondary-foreground/80 no-underline transition-colors hover:text-yellow"
          >
            Terms of Service
          </a>
        </div>
      </div>
    </div>
  </footer>
);

export default LandingFooter;
